/**
 * Settings service.
 *
 * Profile/security/notifications/location remain backed by mock defaults
 * persisted to `localStorage`, with a small simulated latency so the UI
 * exercises real loading/saving states. `AppPreferences` (theme/accent/
 * fontScale/reduceMotion/compactMode/language/region/startScreen) is wired to
 * the real messego-getUserPreferences/messego-updateUserPreferences
 * callables when `USE_BACKEND` is on, with localStorage kept as a
 * write-through cache for instant paint on the next load.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../api/firebase';
import type {
  SettingsState,
  UserProfile,
  AppPreferences,
  SecuritySettings,
  NotificationPreferences,
  LocationSettings,
  DataUsageReport,
  NotificationSound,
  FaqItem,
  ContactPayload,
  ReportPayload,
} from '../../types/settings.types';

const STORAGE_KEY = 'messego.settings.v1';
const LATENCY = 320; // ms — simulate network round-trip

export const USE_BACKEND = import.meta.env.VITE_USE_BACKEND === 'true';

function delay<T>(value: T, ms = LATENCY): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// Wire prefix confirmed against firebase-functions/functions/src/codebases/messego.ts
// (`export * as messego from '../apps/messego'`) — do not confuse with the
// unrelated, unused VITE_MESSEGO_FN_PREFIX env var ("messageApi-"), which
// refers to a disconnected legacy Express module, not these callables.
const FN_PREFIX = 'messego-';
const callable = <I, O>(name: string) => httpsCallable<I, O>(functions, `${FN_PREFIX}${name}`);

// ---------------------------------------------------------------------------
// Mock defaults
// ---------------------------------------------------------------------------

const DEFAULT_PROFILE: UserProfile = {
  displayName: 'Alex Johnson',
  username: 'alex_johnson',
  email: 'alex@example.com',
  phone: '+1 (555) 123-4567',
  bio: 'Passionate product designer focused on creating unified experiences. Building the future of social commerce at Messego.',
  headline: 'Product Designer',
  avatarUrl: 'https://picsum.photos/seed/user/400/400',
  location: 'San Francisco, CA',
  website: 'https://alexjohnson.design',
};

const DEFAULT_PREFERENCES: AppPreferences = {
  theme: 'system',
  accent: '#171717',
  fontScale: 'medium',
  reduceMotion: false,
  compactMode: false,
  language: 'en',
  region: 'US',
  startScreen: 'dashboard',
};

const DEFAULT_SECURITY: SecuritySettings = {
  twoFactorEnabled: false,
  twoFactorMethod: null,
  appLockEnabled: false,
  appLockTimeout: 5,
  biometricEnabled: false,
  loginAlerts: true,
  lastPasswordChange: new Date(Date.now() - 1000 * 60 * 60 * 24 * 142).toISOString(),
  activeSessions: [
    { id: 's1', device: 'MacBook Pro', browser: 'Chrome 128', location: 'San Francisco, US', lastActive: new Date().toISOString(), current: true },
    { id: 's2', device: 'iPhone 15', browser: 'Safari', location: 'San Francisco, US', lastActive: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(), current: false },
    { id: 's3', device: 'Windows PC', browser: 'Edge', location: 'New York, US', lastActive: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(), current: false },
  ],
};

const channel = (push: boolean, email: boolean, sms: boolean) => ({ push, email, sms });

const DEFAULT_NOTIFICATIONS: NotificationPreferences = {
  master: true,
  categories: {
    messages: channel(true, true, false),
    social: channel(true, false, false),
    commerce: channel(true, true, false),
    security: channel(true, true, true),
    marketing: channel(false, false, false),
  },
  sound: 'chime',
  vibration: true,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
};

const DEFAULT_LOCATION: LocationSettings = {
  locationEnabled: true,
  precision: 'precise',
  shareWithBusinesses: false,
  backgroundRefresh: true,
  analyticsEnabled: true,
  personalizedAds: false,
  crashReports: true,
};

function defaults(): SettingsState {
  return {
    profile: { ...DEFAULT_PROFILE },
    preferences: { ...DEFAULT_PREFERENCES },
    security: { ...DEFAULT_SECURITY, activeSessions: DEFAULT_SECURITY.activeSessions.map((s) => ({ ...s })) },
    notifications: structuredClone(DEFAULT_NOTIFICATIONS),
    location: { ...DEFAULT_LOCATION },
  };
}

// ---------------------------------------------------------------------------
// Persistence helpers (mock backend)
// ---------------------------------------------------------------------------

function readStore(): SettingsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = JSON.parse(raw) as Partial<SettingsState>;
    const base = defaults();
    // Shallow-merge each slice so newly-added fields keep their defaults.
    return {
      profile: { ...base.profile, ...parsed.profile },
      preferences: { ...base.preferences, ...parsed.preferences },
      security: { ...base.security, ...parsed.security },
      notifications: { ...base.notifications, ...parsed.notifications },
      location: { ...base.location, ...parsed.location },
    };
  } catch {
    return defaults();
  }
}

function writeStore(state: SettingsState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — non-fatal for a mock backend */
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const settingsService = {
  async getSettings(): Promise<SettingsState> {
    if (USE_BACKEND) {
      const preferences = (await callable<void, AppPreferences>('getUserPreferences')()).data;
      const state = readStore();
      state.preferences = { ...state.preferences, ...preferences };
      writeStore(state);
      return state;
    }
    return delay(readStore());
  },

  async updateProfile(patch: Partial<UserProfile>): Promise<UserProfile> {
    const state = readStore();
    state.profile = { ...state.profile, ...patch };
    writeStore(state);
    return delay(state.profile);
  },

  async updatePreferences(patch: Partial<AppPreferences>): Promise<AppPreferences> {
    if (USE_BACKEND) {
      const preferences = (await callable<Partial<AppPreferences>, AppPreferences>('updateUserPreferences')(patch)).data;
      const state = readStore();
      state.preferences = preferences;
      writeStore(state);
      return preferences;
    }
    const state = readStore();
    state.preferences = { ...state.preferences, ...patch };
    writeStore(state);
    return delay(state.preferences);
  },

  async updateSecurity(patch: Partial<SecuritySettings>): Promise<SecuritySettings> {
    const state = readStore();
    state.security = { ...state.security, ...patch };
    writeStore(state);
    return delay(state.security);
  },

  async revokeSession(sessionId: string): Promise<SecuritySettings> {
    const state = readStore();
    state.security.activeSessions = state.security.activeSessions.filter((s) => s.id !== sessionId);
    writeStore(state);
    return delay(state.security);
  },

  async updateNotifications(patch: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    const state = readStore();
    state.notifications = { ...state.notifications, ...patch };
    writeStore(state);
    return delay(state.notifications);
  },

  async updateLocation(patch: Partial<LocationSettings>): Promise<LocationSettings> {
    const state = readStore();
    state.location = { ...state.location, ...patch };
    writeStore(state);
    return delay(state.location);
  },

  async getDataUsage(): Promise<DataUsageReport> {
    return delay(buildDataUsage());
  },

  async setMediaAutoDownload(mode: DataUsageReport['mediaAutoDownload']): Promise<void> {
    const state = readStore();
    localStorage.setItem(`${STORAGE_KEY}.media`, mode);
    writeStore(state);
    return delay(undefined);
  },

  getNotificationSounds(): NotificationSound[] {
    return [
      { id: 'chime', name: 'Chime', description: 'Soft, bright and friendly' },
      { id: 'pulse', name: 'Pulse', description: 'A gentle two-tone ping' },
      { id: 'ripple', name: 'Ripple', description: 'Calm, watery notification' },
      { id: 'beacon', name: 'Beacon', description: 'Clear and attention-grabbing' },
      { id: 'none', name: 'Silent', description: 'No sound, banner only' },
    ];
  },

  getFaqs(): FaqItem[] {
    return [
      { id: 'f1', category: 'Account', question: 'How do I change my password?', answer: 'Go to Settings → Security → Change Password. You will be asked to confirm your current password before setting a new one.' },
      { id: 'f2', category: 'Account', question: 'How do I enable two-factor authentication?', answer: 'Open Settings → Security → Two-Factor Authentication and follow the setup steps to link an authenticator app or phone number.' },
      { id: 'f3', category: 'Billing', question: 'How do I upgrade to Messego Pro?', answer: 'Tap “Upgrade Now” from your profile or Settings → About. Pro unlocks advanced automation, analytics and priority support.' },
      { id: 'f4', category: 'Privacy', question: 'What data does Messego collect?', answer: 'You control this under Settings → Location & Services. You can disable analytics, personalized ads and precise location at any time.' },
      { id: 'f5', category: 'Notifications', question: 'Why am I not receiving notifications?', answer: 'Check Settings → Notifications. Ensure the master toggle is on and that Quiet Hours is not currently active.' },
      { id: 'f6', category: 'Data', question: 'How do I reduce my data usage?', answer: 'Enable Data Saver under Settings → Location & Services → Data Usage, and set media auto-download to “Wi-Fi only”.' },
    ];
  },

  async submitContact(payload: ContactPayload): Promise<{ ticketId: string }> {
    // if (USE_BACKEND) return callable('submitContact', payload);
    void payload; // consumed by the backend once wired; retained for the mock signature
    return delay({ ticketId: `MSG-${Date.now().toString(36).toUpperCase()}` }, 600);
  },

  async submitReport(payload: ReportPayload): Promise<{ ticketId: string }> {
    // if (USE_BACKEND) return callable('submitReport', payload);
    void payload; // consumed by the backend once wired; retained for the mock signature
    return delay({ ticketId: `RPT-${Date.now().toString(36).toUpperCase()}` }, 600);
  },

  /** Full local reset — used after account deletion. */
  clearLocal(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(`${STORAGE_KEY}.media`);
  },
};

/** Deterministic-ish mock data-usage report for the last 14 days. */
function buildDataUsage(): DataUsageReport {
  const daily = Array.from({ length: 14 }).map((_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (13 - i));
    const wifi = 40 + Math.round(Math.abs(Math.sin(i * 1.3)) * 220);
    const cellular = 8 + Math.round(Math.abs(Math.cos(i * 0.9)) * 90);
    return { date: date.toISOString(), cellular, wifi };
  });
  const cellularMb = daily.reduce((sum, d) => sum + d.cellular, 0);
  const wifiMb = daily.reduce((sum, d) => sum + d.wifi, 0);
  const totalMb = cellularMb + wifiMb;
  return {
    totalMb,
    cellularMb,
    wifiMb,
    breakdown: [
      { label: 'Media', value: Math.round(totalMb * 0.46), color: '#171717' },
      { label: 'Messaging', value: Math.round(totalMb * 0.22), color: '#525252' },
      { label: 'Social', value: Math.round(totalMb * 0.18), color: '#a3a3a3' },
      { label: 'Other', value: Math.round(totalMb * 0.14), color: '#d4d4d4' },
    ],
    daily,
    mediaAutoDownload: (localStorage.getItem(`${STORAGE_KEY}.media`) as DataUsageReport['mediaAutoDownload']) || 'wifi',
    dataSaver: false,
  };
}
